'use client';

import { c as _c } from "react/compiler-runtime";
import { usePostHog } from '@posthog/next';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { PLUS_POSTHOG_EVENTS, PLUS_PRICING_SECTION_ID } from '@/app/[locale]/plus/constants';
import ViverseButton from '@/components/ViverseButton';
import { DEFAULT_VIVERSE_ORIGIN } from '@/constants/domain';
import { useAuthContext } from '@/context/AuthContext';
import { getViversePlusManageSubscriptionUrl, PlusPlan, useViversePlusCheckoutUrl } from '@/hooks/useViversePlusCheckoutUrl';
import { cn } from '@/lib/utils';
import ArrowRight from '@/public/images/viverse-plus/icon_arrow_right.svg';
const plans: PlusPlan[] = ['monthly', 'annual'];
export default function PricingPlansSection() {
  const $ = _c(145);
  const t = useTranslations();
  const {
    isGamePlus
  } = useAuthContext();
  const posthog = usePostHog();
  const [plan, setPlan] = useState("monthly");
  const checkoutUrl = useViversePlusCheckoutUrl(plan);
  let t0;
  if ($[0] !== checkoutUrl || $[1] !== isGamePlus) {
    t0 = isGamePlus ? getViversePlusManageSubscriptionUrl() : checkoutUrl;
    $[0] = checkoutUrl;
    $[1] = isGamePlus;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const finalCheckoutUrl = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: 16
    };
    t2 = {
      opacity: 1,
      y: 0
    };
    t3 = {
      once: true,
      amount: 0.2
    };
    t4 = {
      duration: 0.6,
      ease: "easeOut"
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("plus.pricing.eyebrow");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <p className="text-vive-primary-40 text-xs leading-4 font-extrabold tracking-[6.15px] uppercase">{t5}</p>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isGamePlus || $[12] !== t) {
    t7 = isGamePlus ? t("plus.pricing.manage-title") : t("plus.pricing.title");
    $[11] = isGamePlus;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <h2 className="mt-4 text-3xl leading-10 font-extrabold tracking-[0.15px] text-[#eeeef0] sm:text-[38px]">{t7}</h2>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== plan || $[17] !== t) {
    t9 = plans.map(option => <button key={option} type="button" onClick={() => setPlan(option)} className={cn("relative cursor-pointer rounded-full px-6 py-2 text-xs leading-4 font-bold tracking-[0.22px]", plan === option ? "bg-white/10 text-[#eeeef0]" : "text-vive-primary-30")}>{t(`plus.pricing.toggle.${option}`)}{option === "annual" && <span className="absolute -top-2.5 right-0 rounded-full bg-[linear-gradient(176deg,#c22757_7%,#c961d9_93%)] px-2 py-0.5 text-[8px] leading-3 font-bold tracking-[0.4px] whitespace-nowrap text-white uppercase">{t("plus.pricing.toggle.annual-badge")}</span>}</button>);
    $[16] = plan;
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-white/8 bg-white/6 p-1">{t9}</div>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6 || $[23] !== t8) {
    t11 = <motion.div className="flex flex-col items-center text-center" initial={t1} whileInView={t2} viewport={t3} transition={t4}>{t6}{t8}{t10}</motion.div>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      opacity: 0,
      y: 24
    };
    t13 = {
      opacity: 1,
      y: 0
    };
    t14 = {
      once: true,
      amount: 0.2
    };
    t15 = {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.12
    };
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setPlan("monthly");
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = plan === "monthly" ? "border-transparent bg-[linear-gradient(147deg,rgba(0,179,227,0.08)_0%,rgba(201,97,217,0.08)_100%)] shadow-[0_0_0_1px_rgba(0,179,227,0.15)]" : "border-white/8 bg-white/4";
  let t18;
  if ($[30] !== t17) {
    t18 = cn("relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border p-6 text-left transition-colors", t17);
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div aria-hidden={true} className="pointer-events-none absolute top-0 left-1/2 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00b3e3] opacity-70 blur-3xl" />;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const t20 = plan === "monthly" ? "border-[#00b3e3]" : "border-[#6e7c8e]";
  const t21 = isGamePlus ? "hidden" : "";
  let t22;
  if ($[33] !== t20 || $[34] !== t21) {
    t22 = cn("flex size-4 items-center justify-center rounded-full border", t20, t21);
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== plan) {
    t23 = plan === "monthly" && <span className="size-1.5 rounded-full bg-[#00b3e3]" />;
    $[36] = plan;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t22 || $[39] !== t23) {
    t24 = <span className={t22}>{t23}</span>;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t) {
    t25 = t("plus.pricing.plans.monthly.label");
    $[41] = t;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== t25) {
    t26 = <span className="text-sm leading-[22px] font-semibold text-[#eeeef0]">{t25}</span>;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t24 || $[46] !== t26) {
    t27 = <div className="relative flex items-center gap-2.5">{t24}{t26}</div>;
    $[45] = t24;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t) {
    t28 = t("plus.pricing.plans.monthly.price");
    $[48] = t;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t28) {
    t29 = <p className="relative mt-5 text-[30px] leading-10 font-extrabold tracking-[0.1px] text-[#eeeef0]">{t28}</p>;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] !== t) {
    t30 = t("plus.pricing.plans.monthly.unit");
    $[52] = t;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== t30) {
    t31 = <p className="text-vive-primary-30 relative mt-1 text-xs leading-4 tracking-[0.15px]">{t30}</p>;
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== t) {
    t32 = t("plus.pricing.plans.monthly.note");
    $[56] = t;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== t32) {
    t33 = <div className="relative mt-4 border-t border-white/8 pt-4"><p className="text-vive-primary-30 text-xs leading-4 tracking-[0.15px]">{t32}</p></div>;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t18 || $[61] !== t27 || $[62] !== t29 || $[63] !== t31 || $[64] !== t33) {
    t34 = <button type="button" onClick={t16} className={t18}>{t19}{t27}{t29}{t31}{t33}</button>;
    $[60] = t18;
    $[61] = t27;
    $[62] = t29;
    $[63] = t31;
    $[64] = t33;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== t) {
    t35 = t("plus.pricing.plans.annual.badge");
    $[66] = t;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== t35) {
    t36 = <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-[linear-gradient(176deg,#c22757_7%,#c961d9_93%)] px-3 py-1 text-[9px] leading-3.5 font-bold tracking-[0.4px] whitespace-nowrap text-white uppercase">{t35}</span>;
    $[68] = t35;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => setPlan("annual");
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  const t38 = plan === "annual" ? "border-transparent bg-[linear-gradient(147deg,rgba(0,179,227,0.08)_0%,rgba(201,97,217,0.08)_100%)] shadow-[0_0_0_1px_rgba(0,179,227,0.15)]" : "border-white/8 bg-white/4";
  let t39;
  if ($[71] !== t38) {
    t39 = cn("relative w-full cursor-pointer overflow-hidden rounded-2xl border p-6 text-left transition-colors", t38);
    $[71] = t38;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div aria-hidden={true} className="pointer-events-none absolute top-0 left-1/2 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#c961d9] opacity-90 blur-3xl" />;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  const t41 = plan === "annual" ? "border-[#00b3e3]" : "border-[#6e7c8e]";
  const t42 = isGamePlus ? "hidden" : "";
  let t43;
  if ($[74] !== t41 || $[75] !== t42) {
    t43 = cn("flex size-4 items-center justify-center rounded-full border", t41, t42);
    $[74] = t41;
    $[75] = t42;
    $[76] = t43;
  } else {
    t43 = $[76];
  }
  let t44;
  if ($[77] !== plan) {
    t44 = plan === "annual" && <span className="size-1.5 rounded-full bg-[#00b3e3]" />;
    $[77] = plan;
    $[78] = t44;
  } else {
    t44 = $[78];
  }
  let t45;
  if ($[79] !== t43 || $[80] !== t44) {
    t45 = <span className={t43}>{t44}</span>;
    $[79] = t43;
    $[80] = t44;
    $[81] = t45;
  } else {
    t45 = $[81];
  }
  let t46;
  if ($[82] !== t) {
    t46 = t("plus.pricing.plans.annual.label");
    $[82] = t;
    $[83] = t46;
  } else {
    t46 = $[83];
  }
  let t47;
  if ($[84] !== t46) {
    t47 = <span className="text-sm leading-[22px] font-semibold text-[#eeeef0]">{t46}</span>;
    $[84] = t46;
    $[85] = t47;
  } else {
    t47 = $[85];
  }
  let t48;
  if ($[86] !== t45 || $[87] !== t47) {
    t48 = <div className="relative flex items-center gap-2.5">{t45}{t47}</div>;
    $[86] = t45;
    $[87] = t47;
    $[88] = t48;
  } else {
    t48 = $[88];
  }
  let t49;
  if ($[89] !== t) {
    t49 = t("plus.pricing.plans.annual.price");
    $[89] = t;
    $[90] = t49;
  } else {
    t49 = $[90];
  }
  let t50;
  if ($[91] !== t49) {
    t50 = <p className="relative mt-5 text-[30px] leading-10 font-extrabold tracking-[0.1px] text-[#eeeef0]">{t49}</p>;
    $[91] = t49;
    $[92] = t50;
  } else {
    t50 = $[92];
  }
  let t51;
  if ($[93] !== t) {
    t51 = t("plus.pricing.plans.annual.unit");
    $[93] = t;
    $[94] = t51;
  } else {
    t51 = $[94];
  }
  let t52;
  if ($[95] !== t51) {
    t52 = <p className="text-vive-primary-30 relative mt-1 text-xs leading-4 tracking-[0.15px]">{t51}</p>;
    $[95] = t51;
    $[96] = t52;
  } else {
    t52 = $[96];
  }
  let t53;
  if ($[97] !== t) {
    t53 = t("plus.pricing.plans.annual.note");
    $[97] = t;
    $[98] = t53;
  } else {
    t53 = $[98];
  }
  let t54;
  if ($[99] !== t53) {
    t54 = <p className="text-vive-primary-30 text-xs leading-4 tracking-[0.15px]">{t53}</p>;
    $[99] = t53;
    $[100] = t54;
  } else {
    t54 = $[100];
  }
  let t55;
  if ($[101] !== t) {
    t55 = t("plus.pricing.plans.annual.cancel");
    $[101] = t;
    $[102] = t55;
  } else {
    t55 = $[102];
  }
  let t56;
  if ($[103] !== t55) {
    t56 = <p className="text-vive-primary-30 text-xs leading-4 tracking-[0.15px]">{t55}</p>;
    $[103] = t55;
    $[104] = t56;
  } else {
    t56 = $[104];
  }
  let t57;
  if ($[105] !== t54 || $[106] !== t56) {
    t57 = <div className="relative mt-4 flex flex-col gap-1 border-t border-white/8 pt-4">{t54}{t56}</div>;
    $[105] = t54;
    $[106] = t56;
    $[107] = t57;
  } else {
    t57 = $[107];
  }
  let t58;
  if ($[108] !== t39 || $[109] !== t48 || $[110] !== t50 || $[111] !== t52 || $[112] !== t57) {
    t58 = <button type="button" onClick={t37} className={t39}>{t40}{t48}{t50}{t52}{t57}</button>;
    $[108] = t39;
    $[109] = t48;
    $[110] = t50;
    $[111] = t52;
    $[112] = t57;
    $[113] = t58;
  } else {
    t58 = $[113];
  }
  let t59;
  if ($[114] !== t36 || $[115] !== t58) {
    t59 = <div className="relative">{t36}{t58}</div>;
    $[114] = t36;
    $[115] = t58;
    $[116] = t59;
  } else {
    t59 = $[116];
  }
  let t60;
  if ($[117] !== t34 || $[118] !== t59) {
    t60 = <motion.div className="mx-auto mt-12 grid max-w-3xl gap-4 md:grid-cols-2" initial={t12} whileInView={t13} viewport={t14} transition={t15}>{t34}{t59}</motion.div>;
    $[117] = t34;
    $[118] = t59;
    $[119] = t60;
  } else {
    t60 = $[119];
  }
  let t61;
  let t62;
  let t63;
  let t64;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = {
      opacity: 0,
      y: 16
    };
    t62 = {
      opacity: 1,
      y: 0
    };
    t63 = {
      once: true,
      amount: 0.2
    };
    t64 = {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.2
    };
    $[120] = t61;
    $[121] = t62;
    $[122] = t63;
    $[123] = t64;
  } else {
    t61 = $[120];
    t62 = $[121];
    t63 = $[122];
    t64 = $[123];
  }
  let t65;
  if ($[124] !== posthog) {
    t65 = () => posthog?.capture(PLUS_POSTHOG_EVENTS.TRIAL_CLICK);
    $[124] = posthog;
    $[125] = t65;
  } else {
    t65 = $[125];
  }
  let t66;
  if ($[126] !== isGamePlus || $[127] !== t) {
    t66 = isGamePlus ? t("plus.cta.manage-subscription") : t("plus.pricing.cta");
    $[126] = isGamePlus;
    $[127] = t;
    $[128] = t66;
  } else {
    t66 = $[128];
  }
  let t67;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <ArrowRight aria-hidden={true} className="size-7" />;
    $[129] = t67;
  } else {
    t67 = $[129];
  }
  let t68;
  if ($[130] !== finalCheckoutUrl || $[131] !== t65 || $[132] !== t66) {
    t68 = <ViverseButton asChild={true} size="medium" variant="colorful" className="min-h-12 px-6 text-base"><a href={finalCheckoutUrl} target="_blank" rel="noreferrer" onClick={t65}>{t66}{t67}</a></ViverseButton>;
    $[130] = finalCheckoutUrl;
    $[131] = t65;
    $[132] = t66;
    $[133] = t68;
  } else {
    t68 = $[133];
  }
  let t69;
  if ($[134] !== t) {
    t69 = t.rich("plus.pricing.disclaimer", {
      terms: _temp
    });
    $[134] = t;
    $[135] = t69;
  } else {
    t69 = $[135];
  }
  let t70;
  if ($[136] !== t69) {
    t70 = <p className="text-vive-primary-40 mt-4 max-w-[759px] text-xs leading-4">{t69}</p>;
    $[136] = t69;
    $[137] = t70;
  } else {
    t70 = $[137];
  }
  let t71;
  if ($[138] !== t68 || $[139] !== t70) {
    t71 = <motion.div className="mt-10 flex flex-col items-center text-center" initial={t61} whileInView={t62} viewport={t63} transition={t64}>{t68}{t70}</motion.div>;
    $[138] = t68;
    $[139] = t70;
    $[140] = t71;
  } else {
    t71 = $[140];
  }
  let t72;
  if ($[141] !== t11 || $[142] !== t60 || $[143] !== t71) {
    t72 = <div id={PLUS_PRICING_SECTION_ID} className="overflow-hidden px-6 py-16 sm:px-12 sm:py-20"><div className="mx-auto max-w-[1200px] border-t border-white/8 pt-16">{t11}{t60}{t71}</div></div>;
    $[141] = t11;
    $[142] = t60;
    $[143] = t71;
    $[144] = t72;
  } else {
    t72 = $[144];
  }
  return t72;
}
function _temp(chunks) {
  return <a href={`${DEFAULT_VIVERSE_ORIGIN}/terms-of-use`} target="_blank" rel="noreferrer" className="font-semibold text-white underline-offset-2 hover:underline">{chunks}</a>;
}